// src/features/system/developer/components/simulations/table-ops/api/tableOpsApi.ts
// ────────────────────────────────────────────────────────────────────────────
// TableOps frontend API layer — typed client for the BFF /table-ops endpoints.
//
// Uses the generic `bff` client (respects session, CSRF, error handling).
// Endpoint paths match server/bff/modules/services/table-ops/routes.mjs.
//
// Internal fields (tenant_id, scope_namespace, scope_key, id_scope) are
// NEVER exposed to the FE.  Digested endpoints return clean, branded IDs.
// ────────────────────────────────────────────────────────────────────────────

import { bff } from '@/lib/http/bff';
import type { StationId, TableOrder } from '../orderTypes';
import type { CheckPayment, TableCloseRecord, RegisterShift } from '../types';
import type { TableSession } from '../useOrderStore';

/* ─── Types ─────────────────────────────────────────────────────────── */

/** Scope entity as returned by orchestrator (raw — used by generic catalog / admin) */
export interface ScopeEntity {
  /** Domain key — stable human-readable identifier (e.g. "table_1", "carta_1") */
  key: string;
  /** Parent's domain key — resolved at BFF from scope hierarchy (null for root entities) */
  parent_key?: string | null;
  /** Timestamp of last modification */
  modified_at?: string;
  /** All remaining domain attributes are spread flat (no nesting in `attributes`) */
  [attr: string]: unknown;
}

/** Generic catalog list response — returned by /catalog/:namespace and digested endpoints */
export interface CatalogListResponse<T = ScopeEntity> {
  items: T[];
  next_cursor: string | null;
}

/** Digested menu-item — FE-friendly shape from GET /items */
export interface CatalogMenuItem {
  id: string;
  name: string;
  description: string;
  price: number;
  section_id: number | null;
  section: string;
  carta: string;
  station: string;
  image_url: string;
  prep_s: number;
  active: boolean;
  registered_at?: string;
}

/** Digested carta — FE-friendly shape from GET /cartas */
export interface CatalogCarta {
  id: string;
  name: string;
  description: string;
  schedule: string;
  active: boolean;
  registered_at?: string;
}

/** Digested section — FE-friendly shape from GET /sections */
export interface CatalogSection {
  id: string;
  name: string;
  carta_id: number | null;
  display_order: number;
  icon: string;
  active: boolean;
  registered_at?: string;
}

/** Digested table — FE-friendly shape from GET /tables */
export interface CatalogTable {
  key: string;
  label: string;
  seats: number | null;
  shape: string | null;
  color: string | null;
  active: boolean;
}

/** Digested register (cashier) — FE-friendly shape from GET /registers */
export interface CatalogRegister {
  key: string;
  number: number;
  label: string;
  payment_method_ids: string[];
  active: boolean;
}

/** Digested payment method — FE-friendly shape from GET /payment-methods */
export interface CatalogPaymentMethod {
  key: string;
  label: string;
  icon: string;
  category: string;
  network: string;
  active: boolean;
}

/** Order trigger request payload */
export interface TriggerOrderInput {
  table_id: string;
  lines: OrderLineInput[];
  total: number;
}

export interface OrderLineInput {
  id_item: number;
  /** Stable scope key for the menu item (SKID canonical ID) */
  item_key: string;
  name: string;
  description: string;
  price: number;
  quantity: number;
  station: StationId;
  section: string;
  /** Which check (split-bill) this line belongs to (0-based). Populated for multi-check orders. */
  check_index?: number;
}

/** Workflow start response */
export interface TriggerOrderResponse {
  workflow_run_id: string;
  workflow_id?: string;
  status: string;
}

/** Table detail — aggregated view with orders + tickets */
export interface TableDetail {
  table: ScopeEntity | null;
  orders: OrderWithTickets[];
}

export interface OrderWithTickets extends ScopeEntity {
  tickets: TicketScope[];
}

export interface TicketScope {
  id_scope_ticket: number;
  station: string;
  ticket_scope_key: string;
  created_at: string;
  updated_at: string;
}

/** Outcome event (compact) */
export interface OutcomeEvent {
  outcome_id: number;
  id_scope: number;
  outcome_key: string;
  value: unknown;
  created_at: string;
  handler_key?: string;
  id_category: number;
  id_type: number;
}

export interface BumpTicketInput {
  order_id: string;
  ticket_id: string;
  station: StationId;
  status?: 'in_progress' | 'ready' | 'delivered' | 'closed';
}

export interface VoidOrderLineInput {
  order_id: string;
  ticket_id: string;
  line_index: number;
  station?: StationId;
  item_key?: string;
  name?: string;
  note?: string;
  quantity?: number;
  price?: number;
}

export interface VoidOrderLineResponse {
  order_id: string;
  ticket_id: string;
  line_index: number;
  voided_at: string;
  visibility?: 'confirmed' | 'pending';
}

/** Minimal shift payload used by floorplan gate checks */
export interface ShiftScopeEntity {
  key: string;
  status?: 'open' | 'closed' | string;
  label?: string;
  register_number?: number;
  opening_float?: number;
  opened_at?: string;
  closed_at?: string | null;
}

export interface ShiftScopeListResponse {
  items: ShiftScopeEntity[];
  next_cursor?: string | null;
}

/** Input for opening a cashier shift scope/event */
export interface OpenShiftInput {
  shiftId: string;
  label?: string;
  registerNumber: number;
  openingFloat: number;
  openedAt?: string;
  tenantId?: number;
}

/** Input for closing a cashier shift scope/event */
export interface CloseShiftInput {
  shiftId: string;
  label?: string;
  registerNumber: number;
  openedAt?: string;
  closedAt?: string;
  tenantId?: number;
}

/** Generic shift mutation response */
export interface ShiftMutationResponse {
  shift_key: string;
  register_number?: number;
  run: unknown;
}

/** Payment method breakdown in shift analytics */
export interface PaymentBreakdown {
  method: string;
  count: number;
  amount: number;
  tips: number;
  pct: number;
}

/** Hourly revenue timeline in shift analytics */
export interface HourlyTimeline {
  hour: number;
  revenue: number;
  count: number;
}

/** Enriched shift history card for cashier display */
export interface ShiftHistoryCard {
  key: string;
  label: string;
  register_number: number;
  opening_float: number;
  opened_at: string;
  closed_at: string;
  time_ago: string;
  duration_ms: number;
  sessions_count: number;
  tables_count: number;
  takeaway_count: number;
  delivery_count: number;
  orders_count: number;
  total_revenue: number;
  total_tips: number;
  avg_check: number;
  tip_rate: number;
  payment_distribution: PaymentBreakdown[];
  hourly_timeline: HourlyTimeline[];
}

export interface ShiftHistoryResponse {
  items: ShiftHistoryCard[];
}

export interface CurrentShiftSummary {
  total_revenue: number;
  total_tips: number;
  table_count: number;
  active_session_count: number;
  order_count: number;
}

export interface CashierSummaryResponse {
  generated_at: string;
  open_register: RegisterShift | null;
  current_shift_summary: CurrentShiftSummary | null;
  recent_closed_shifts: ShiftHistoryCard[];
  projection_status: {
    state: 'ready' | 'pending_projection' | 'degraded' | 'empty';
    freshness: 'live' | 'cached' | 'stale' | 'offline';
    namespace: string;
    projection_key: string;
    version: string;
    updated_at: string | null;
    age_ms: number;
    last_event_seq: number | null;
    stale_while_revalidate: boolean;
    served_from: 'origin' | 'cache' | 'stale-cache' | 'miss';
  };
}

/* ─── Session types ─────────────────────────────────────────────────── */

/** Input for opening a hot table session */
export interface OpenSessionInput {
  table_key: string;
  checks?: number;
  covers?: number;
}

/** Response from POST /sessions/open */
export interface OpenSessionResponse {
  session_key: string;
  table_key: string;
  opened_at: string;
  checks: number;
  covers: number;
  run: unknown;
}

/** Input for closing a hot table session */
export interface CloseSessionInput {
  session_key: string;
  total?: number;
}

/** Response from POST /sessions/close */
export interface CloseSessionResponse {
  session_key: string;
  closed_at: string;
  run: unknown;
}

/** Enhanced order trigger input — includes session context */
export interface TriggerOrderWithSessionInput extends TriggerOrderInput {
  session_key?: string;
  table_key?: string;
  // check_index is now per-line (lines[].check_index) for multi-check support
}

/** Enhanced order trigger response — includes scope result */
export interface TriggerOrderWithSessionResponse {
  pipeline?: TriggerOrderResponse;
  scope?: {
    order_key: string;
    session_key: string;
    table_key: string;
    placed_at: string;
    total: number;
    run: unknown;
  };
}

export interface PlaceFulfillmentOrderInput {
  scope_key: string;
  fulfillment_type: 'delivery' | 'takeaway';
  shift_id?: string;
  customer_name: string;
  customer_phone?: string;
  address?: string;
  notes?: string;
  lines: Array<{
    id: number;
    scopeKey?: string;
    name: string;
    description: string;
    price: number;
    quantity: number;
    section: string;
    station?: StationId;
    note?: string;
  }>;
  total: number;
}

export interface PlaceFulfillmentOrderResponse {
  order_key: string;
  placed_at: string;
  total: number;
  fulfillment_type: 'delivery' | 'takeaway';
  scope?: unknown;
  stream?: unknown;
}

export interface MarkFulfillmentStageInput {
  scope_key: string;
  order_id: string;
  stage: 'accepted' | 'ready' | 'handed_off' | 'completed';
  at?: string;
  fulfillment_type?: 'delivery' | 'takeaway';
}

export interface MarkFulfillmentStageResponse {
  scope_key: string;
  order_id: string;
  stage: 'accepted' | 'ready' | 'handed_off' | 'completed';
  at: string;
  stream?: unknown;
}

/* ─── API calls ─────────────────────────────────────────────────────── */

const BASE = '/table-ops';

/**
 * Trigger the tableops_order_pipeline workflow.
 * Enhanced: if session_key + table_key are provided, also creates an order scope.
 * POST /bff/table-ops/orders
 */
export const triggerOrderPipeline = (
  input: TriggerOrderWithSessionInput,
): Promise<TriggerOrderWithSessionResponse> =>
  bff.post<TriggerOrderWithSessionResponse>(`${BASE}/orders`, input);

/**
 * Persist a takeaway / delivery order as a durable hot-stream event.
 * POST /bff/table-ops/fulfillment/orders
 */
export const registerFulfillmentOrder = (
  input: PlaceFulfillmentOrderInput,
): Promise<PlaceFulfillmentOrderResponse> =>
  bff.post<PlaceFulfillmentOrderResponse>(`${BASE}/fulfillment/orders`, input);

/**
 * Persist a fulfillment lifecycle stage transition as durable hot-stream event.
 * POST /bff/table-ops/fulfillment/stage
 */
export const registerFulfillmentStage = (
  input: MarkFulfillmentStageInput,
): Promise<MarkFulfillmentStageResponse> =>
  bff.post<MarkFulfillmentStageResponse>(`${BASE}/fulfillment/stage`, input);

/**
 * Open a hot table session — creates a tableops.session scope under the table.
 * POST /bff/table-ops/sessions/open
 */
export const openTableSession = (input: OpenSessionInput): Promise<OpenSessionResponse> =>
  bff.post<OpenSessionResponse>(`${BASE}/sessions/open`, input);

/**
 * Close a hot table session — records closing outcome facts.
 * POST /bff/table-ops/sessions/close
 */
export const closeTableSession = (input: CloseSessionInput): Promise<CloseSessionResponse> =>
  bff.post<CloseSessionResponse>(`${BASE}/sessions/close`, input);

/* ─── Payment outcome types ─────────────────────────────────────────── */

/** Input for registering a payment as an outcome on the session scope */
export interface RegisterPaymentInput {
  session_key: string;
  table_key: string;
  check_index: number;
  payment_method: string;       // e.g. "cash", "visa", "mercadopago_qr"
  amount: number;
  tip: number;
  paid_at: string;              // ISO
  shift_id?: string;
  fulfillment_type?: 'delivery' | 'takeaway' | 'table';
}

/** Response from POST /payments/register */
export interface RegisterPaymentResponse {
  payment_key: string;
  session_key: string;
  run: unknown;
}

/**
 * Register a payment as an outcome on the table session scope.
 * Payments are pipeline outcomes — they hang from the register shift.
 * POST /bff/table-ops/payments/register
 */
export const registerPaymentOutcome = (
  input: RegisterPaymentInput,
): Promise<RegisterPaymentResponse> =>
  bff.post<RegisterPaymentResponse>(`${BASE}/payments/register`, input);

/**
 * List all table scopes.
 * GET /bff/table-ops/tables
 */
export const fetchTables = (): Promise<ScopeEntity[]> =>
  bff.get<ScopeEntity[]>(`${BASE}/tables`);

/**
 * Get aggregated table detail: table scope + orders + tickets.
 * GET /bff/table-ops/tables/:tableKey
 */
export const fetchTableDetail = (tableKey: string): Promise<TableDetail> =>
  bff.get<TableDetail>(`${BASE}/tables/${encodeURIComponent(tableKey)}`);

/**
 * List orders for a specific table.
 * GET /bff/table-ops/tables/:tableKey/orders
 */
export const fetchTableOrders = (
  tableKey: string,
  limit = 20,
): Promise<{ items: ScopeEntity[]; table_scope: ScopeEntity | null }> =>
  bff.get(`${BASE}/tables/${encodeURIComponent(tableKey)}/orders`, { limit });

/**
 * List tickets for a specific order.
 * GET /bff/table-ops/orders/:orderScopeId/tickets
 */
export const fetchOrderTickets = (orderScopeId: number): Promise<ScopeEntity[]> =>
  bff.get<ScopeEntity[]>(`${BASE}/orders/${orderScopeId}/tickets`);

/**
 * Bump a ticket (record outcome fact for station completion).
 * POST /bff/table-ops/tickets/bump
 */
export const bumpTicketApi = (input: BumpTicketInput): Promise<unknown> =>
  bff.post(`${BASE}/tickets/bump`, input);

/**
 * Void a single line on an order ticket as a durable order event.
 * POST /bff/table-ops/orders/void-line
 */
export const voidOrderLineApi = (
  input: VoidOrderLineInput,
): Promise<VoidOrderLineResponse> =>
  bff.post<VoidOrderLineResponse>(`${BASE}/orders/void-line`, input);

/**
 * List shifts with optional status filter.
 * GET /bff/table-ops/shifts
 */
export const fetchShifts = (
  status?: 'open' | 'closed',
  tenantId?: number,
): Promise<ShiftScopeListResponse> =>
  bff.request<ShiftScopeListResponse>(`${BASE}/shifts`, {
    method: 'GET',
    qs: {
      ...(status ? { status } : {}),
    },
    // Tenant scoping is resolved by BFF session/IAM context.
    headers: undefined,
  } as any);

/**
 * Get shift history cards with analytics for closed shifts from the last 7 days.
 * Returns enriched cards suitable for cashier history display.
 * GET /bff/table-ops/shifts/history
 */
export const fetchShiftHistory = (
  daysBack: number = 7,
  limit: number = 100,
  tenantId?: number,
): Promise<ShiftHistoryResponse> =>
  bff.request<ShiftHistoryResponse>(`${BASE}/shifts/history`, {
    method: 'GET',
    qs: {
      daysBack,
      limit,
    },
    // Tenant scoping is resolved by BFF session/IAM context.
    headers: undefined,
  } as any);

/**
 * Open a cashier shift in the backend shift ledger.
 * POST /bff/table-ops/shifts/open
 */
export const openShift = (input: OpenShiftInput): Promise<ShiftMutationResponse> =>
  // Do not forward tenant selectors from FE; BFF derives effective tenant.
  bff.request<ShiftMutationResponse>(`${BASE}/shifts/open`, {
    method: 'POST',
    body: JSON.stringify(((i) => {
      const { tenantId: _tenantId, ...rest } = i;
      return rest;
    })(input)),
    headers: undefined,
  } as any);

/**
 * Close a cashier shift in the backend shift ledger.
 * POST /bff/table-ops/shifts/close
 */
export const closeShift = (input: CloseShiftInput): Promise<ShiftMutationResponse> =>
  // Do not forward tenant selectors from FE; BFF derives effective tenant.
  bff.request<ShiftMutationResponse>(`${BASE}/shifts/close`, {
    method: 'POST',
    body: JSON.stringify(((i) => {
      const { tenantId: _tenantId, ...rest } = i;
      return rest;
    })(input)),
    headers: undefined,
  } as any);

/**
 * Query outcomes by scope namespace + key.
 * GET /bff/table-ops/outcomes?scope_namespace=...&scope_key=...
 */
export const fetchOrderOutcomes = (
  scopeNamespace: string,
  scopeKey?: string,
): Promise<OutcomeEvent[]> =>
  bff.get<OutcomeEvent[]>(`${BASE}/outcomes`, {
    scope_namespace: scopeNamespace,
    ...(scopeKey ? { scope_key: scopeKey } : {}),
  });

/* ─── Entity registration (via entity.register workflow) ───────────── */

/** Input for ensuring a table scope exists via the entity.register pipeline */
export interface EnsureTableScopeInput {
  table_key: string;
}

/** Input for ensuring a frame scope exists via the entity.register pipeline */
export interface EnsureFrameScopeInput {
  frame_key: string;
}

/** A single menu-item record sent for scope registration */
export interface BootstrapMenuItem {
  id: number;
  scopeKey: string;
  name: string;
  description: string;
  price: number;
  section: string;
  station: string;
  active?: boolean;
}

/** Response from the menu-items registration endpoint */
export interface BootstrapMenuItemsResponse {
  registered: number;
  runs: TriggerOrderResponse[];
}

/**
 * Register a table scope via entity.register workflow pipeline.
 * POST /bff/table-ops/scopes/table
 */
export const ensureTableScope = (input: EnsureTableScopeInput): Promise<TriggerOrderResponse> =>
  bff.post<TriggerOrderResponse>(`${BASE}/scopes/table`, input);

/**
 * Register a frame scope via entity.register workflow pipeline.
 * POST /bff/table-ops/scopes/frame
 */
export const ensureFrameScope = (input: EnsureFrameScopeInput): Promise<TriggerOrderResponse> =>
  bff.post<TriggerOrderResponse>(`${BASE}/scopes/frame`, input);

/**
 * Register menu-item scopes + catalog attributes via entity.register pipeline.
 * POST /bff/table-ops/scopes/items
 */
export const bootstrapMenuItems = (
  items: BootstrapMenuItem[],
): Promise<BootstrapMenuItemsResponse> =>
  bff.post<BootstrapMenuItemsResponse>(`${BASE}/scopes/items`, { items });

/** A single carta record for scope registration */
export interface BootstrapCarta {
  id: string;
  scopeKey: string;
  name: string;
  description?: string;
  schedule?: string;
  active?: boolean;
}

/** A single section record for scope registration */
export interface BootstrapSection {
  id: string;
  scopeKey: string;
  name: string;
  carta: string;
  display_order?: number;
  icon?: string;
  active?: boolean;
}

/**
 * Register carta scopes via entity.register workflow pipeline.
 * POST /bff/table-ops/scopes/cartas
 */
export const bootstrapCartas = (
  cartas: BootstrapCarta[],
): Promise<BootstrapMenuItemsResponse> =>
  bff.post<BootstrapMenuItemsResponse>(`${BASE}/scopes/cartas`, { cartas });

/**
 * Register section scopes via entity.register workflow pipeline.
 * POST /bff/table-ops/scopes/sections
 */
export const bootstrapSections = (
  sections: BootstrapSection[],
): Promise<BootstrapMenuItemsResponse> =>
  bff.post<BootstrapMenuItemsResponse>(`${BASE}/scopes/sections`, { sections });

/** A single register record for scope registration */
export interface BootstrapRegister {
  number: number;
  scopeKey?: string;
  label?: string;
  payment_method_ids?: string[];
  active?: boolean;
}

/**
 * Register cashier register scopes via entity.register workflow pipeline.
 * POST /bff/table-ops/scopes/registers
 */
export const bootstrapRegisters = (
  registers: BootstrapRegister[],
): Promise<BootstrapMenuItemsResponse> =>
  bff.post<BootstrapMenuItemsResponse>(`${BASE}/scopes/registers`, { registers });

/** A single payment method category for scope registration */
export interface BootstrapPaymentMethodCategory {
  id: string;
  scopeKey?: string;
  name: string;
  icon?: string;
  description?: string;
  active?: boolean;
}

/**
 * Register payment method category scopes via entity.register workflow.
 * POST /bff/table-ops/scopes/payment-method-categories
 */
export const bootstrapPaymentMethodCategories = (
  categories: BootstrapPaymentMethodCategory[],
): Promise<BootstrapMenuItemsResponse> =>
  bff.post<BootstrapMenuItemsResponse>(`${BASE}/scopes/payment-method-categories`, { categories });

/** A single payment method record for scope registration */
export interface BootstrapPaymentMethod {
  id: string;
  scopeKey?: string;
  label: string;
  icon?: string;
  category?: string;
  network?: string;
  active?: boolean;
}

/**
 * Register payment method scopes via entity.register workflow pipeline.
 * POST /bff/table-ops/scopes/payment-methods
 */
export const bootstrapPaymentMethods = (
  methods: BootstrapPaymentMethod[],
): Promise<BootstrapMenuItemsResponse> =>
  bff.post<BootstrapMenuItemsResponse>(`${BASE}/scopes/payment-methods`, { methods });

/**
 * Update (re-register) any entity — creates a new version (latest timestamp wins).
 * POST /bff/table-ops/scopes/:sectionRoute
 *
 * @param sectionRoute - e.g. "registers", "payment-methods", "cartas", etc.
 * @param bodyKey      - payload wrapper key e.g. "registers", "methods", "cartas"
 * @param items        - array of entity payloads
 */
export const updateEntity = (
  sectionRoute: string,
  bodyKey: string,
  items: Record<string, unknown>[],
): Promise<BootstrapMenuItemsResponse> =>
  bff.post<BootstrapMenuItemsResponse>(`${BASE}/scopes/${sectionRoute}`, { [bodyKey]: items });

/* ─── Attribute patching (targeted backfill) ───────────────────────── */

/** A single attribute-patch item — key + partial attributes to upsert */
export interface PatchAttributeItem {
  key: string;
  attributes: Record<string, unknown>;
}

/** Detail row returned for each patch after server-side diff */
export interface PatchDetail {
  key: string;
  action: 'updated' | 'skipped' | 'not_found';
  prev?: Record<string, unknown>;
  next?: Record<string, unknown>;
  reason?: string;
}

/** Response from the patch-attributes endpoint */
export interface PatchAttributesResponse {
  updated: number;
  skipped: number;
  not_found: number;
  details: PatchDetail[];
}

/**
 * Patch specific attributes on existing menu-item entities.
 * Server-side diff: same value → skip, different → upsert, missing key → not_found.
 *
 * POST /bff/table-ops/scopes/patch-attributes
 */
export const patchItemAttributes = (
  patches: PatchAttributeItem[],
): Promise<PatchAttributesResponse> =>
  bff.post<PatchAttributesResponse>(`${BASE}/scopes/patch-attributes`, { patches });

/* ─── Generic catalog (Layer 2 — DRY scope-entity reader) ──────────── */

/**
 * List ALL scope entities in a namespace, enriched with outcome attributes.
 * Works for ANY namespace: tableops.item, tableops.table, tableops.order, etc.
 *
 * GET /bff/table-ops/catalog/:namespace
 */
export const fetchCatalog = (
  namespace: string,
  opts: { limit?: number; cursor?: string; parent?: number; enrich?: boolean } = {},
): Promise<CatalogListResponse<ScopeEntity>> =>
  bff.get<CatalogListResponse<ScopeEntity>>(
    `${BASE}/catalog/${encodeURIComponent(namespace)}`,
    opts,
  );

/**
 * Get a single scope entity by namespace + key, enriched with attributes.
 *
 * GET /bff/table-ops/catalog/:namespace/:key
 */
export const fetchCatalogEntity = (
  namespace: string,
  key: string,
): Promise<ScopeEntity> =>
  bff.get<ScopeEntity>(
    `${BASE}/catalog/${encodeURIComponent(namespace)}/${encodeURIComponent(key)}`,
  );

/**
 * Fetch ALL namespaces in a single HTTP call with parent_key resolved.
 * Returns an object keyed by short section id (e.g. floorplan, frame, table…).
 *
 * GET /bff/table-ops/catalog/snapshot
 */
export type CatalogSnapshot = Record<string, ScopeEntity[]>;

export const fetchCatalogSnapshot = (
  opts?: Record<string, string | number | boolean | undefined>,
): Promise<CatalogSnapshot> =>
  bff.get<CatalogSnapshot>(
    `${BASE}/catalog/snapshot`,
    ((raw) => {
      if (!raw) return undefined;
      const { tenant_id: _tenant_id, tenantId: _tenantId, ...rest } = raw as any;
      return rest;
    })(opts) as any,
  );

/* ─── Operational live-state (server-authoritative cross-user sync) ──── */

/**
 * Server-reconstructed operational state in FE-ready shapes.
 * Mirrors the reducer's `OrderState` operational slices so it can be merged
 * directly. All ids are deterministic (`order_key::c{check}` /
 * `order_key::c{check}::{station}`) so per-ticket bumps correlate across
 * clients without shared random uuids.
 */
export interface OperationsSnapshot {
  /** ISO timestamp the snapshot was reconstructed — optimistic-merge cutoff. */
  generated_at: string;
  /** Active (open) sessions keyed implicitly by table — flat list. */
  sessions: TableSession[];
  /** Orders belonging to active sessions (already split per check). */
  orders: TableOrder[];
  /** Payments registered against active sessions. */
  payments: CheckPayment[];
  /** Finalized table-close records (closed sessions) for history/ledger. */
  closedTables: TableCloseRecord[];
  /**
   * Active (open) cashier register shift reconstructed from hot_stream, or
   * null when no shift is open. Server-authoritative so the register survives
   * reloads and converges across every operator on the tenant.
   */
  register?: RegisterShift | null;
}

/** SSE `snapshot` event payload — OperationsSnapshot plus a monotonic seq. */
export interface OperationsStreamSnapshot extends OperationsSnapshot {
  seq: number;
}

/**
 * Fetch the full reconstructed operational state in a single HTTP call.
 * Reliable initial-load + SSE-fallback complement to /operations/stream.
 *
 * GET /bff/table-ops/operations/snapshot
 */
export const fetchOperationsSnapshot = (
  opts?: { from?: string; limit?: number; tenant_id?: number },
): Promise<OperationsSnapshot> =>
  bff.request<OperationsSnapshot>(`${BASE}/operations/snapshot`, {
    method: 'GET',
    qs: ((raw) => {
      if (!raw) return undefined;
      const { tenant_id: _tenant_id, ...rest } = raw;
      return rest;
    })(opts),
    headers: undefined,
  } as any);

export const fetchCashierSummary = (
  opts?: { historyDays?: number; historyLimit?: number; from?: string; limit?: number; tenant_id?: number },
): Promise<CashierSummaryResponse> =>
  bff.request<CashierSummaryResponse>(`${BASE}/cashier/summary`, {
    method: 'GET',
    qs: ((raw) => {
      if (!raw) return undefined;
      const { tenant_id: _tenant_id, ...rest } = raw;
      return rest;
    })(opts),
    headers: undefined,
  } as any);

/* ─── FE-digested convenience (Layer 3) ─────────────────────────────── */

/**
 * Fetch the digested menu-item catalog — flat FE-friendly objects.
 * GET /bff/table-ops/items
 */
export const fetchMenuCatalog = (
  opts: { limit?: number; cursor?: string; tenant_id?: number | string } = {},
): Promise<CatalogListResponse<CatalogMenuItem>> =>
  bff.request<CatalogListResponse<CatalogMenuItem>>(`${BASE}/items`, {
    method: 'GET',
    qs: ((raw) => {
      const { tenant_id: _tenant_id, ...rest } = raw;
      return rest;
    })(opts),
    headers: undefined,
  } as any);

/**
 * Fetch the digested carta catalog — flat FE-friendly objects.
 * GET /bff/table-ops/cartas
 */
export const fetchCartaCatalog = (
  opts: { limit?: number; cursor?: string } = {},
): Promise<CatalogListResponse<CatalogCarta>> =>
  bff.get<CatalogListResponse<CatalogCarta>>(`${BASE}/cartas`, opts);

/**
 * Fetch the digested section catalog — flat FE-friendly objects.
 * GET /bff/table-ops/sections
 */
export const fetchSectionCatalog = (
  opts: { limit?: number; cursor?: string; carta?: string } = {},
): Promise<CatalogListResponse<CatalogSection>> =>
  bff.get<CatalogListResponse<CatalogSection>>(`${BASE}/sections`, opts);

/**
 * Fetch the digested table catalog — flat FE-friendly objects.
 * GET /bff/table-ops/tables  (now returns digested shape)
 */
export const fetchTableCatalog = (): Promise<CatalogListResponse<CatalogTable>> =>
  bff.get<CatalogListResponse<CatalogTable>>(`${BASE}/tables`);

/**
 * Fetch the digested register (cashier) catalog — clean FE-friendly objects.
 * GET /bff/table-ops/registers
 *
 * Returns only public fields: key, number, label, payment_method_ids, active.
 * No internal DB IDs (id_scope, id_user) are exposed.
 */
export const fetchRegisterCatalog = (
  opts?: { tenant_id?: number | string },
): Promise<CatalogListResponse<CatalogRegister>> =>
  bff.request<CatalogListResponse<CatalogRegister>>(`${BASE}/registers`, {
    method: 'GET',
    headers: undefined,
  } as any);

/**
 * Fetch the digested payment method catalog — clean FE-friendly objects.
 * GET /bff/table-ops/payment-methods
 */
export const fetchPaymentMethodCatalog = (
  opts?: { tenant_id?: number | string },
): Promise<CatalogListResponse<CatalogPaymentMethod>> =>
  bff.request<CatalogListResponse<CatalogPaymentMethod>>(`${BASE}/payment-methods`, {
    method: 'GET',
    headers: undefined,
  } as any);
